import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import api from '../api/client';
import { useAuth, hasRole } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PageHeader } from '../components/PageHeader';
import {
  Clock, Fuel, Gauge, Activity, Plus, Download, RefreshCw, Save, Trash2,
  Wifi, WifiOff, Sparkles, TrendingUp, AlertTriangle, BarChart3, Sun, Moon,
} from 'lucide-react';
import { formatDate, formatNumber } from '../lib/format';

const SHIFTS = [
  { v: 'day', l: 'Day', icon: Sun },
  { v: 'night', l: 'Night', icon: Moon },
  { v: 'swing', l: 'Swing', icon: Clock },
  { v: 'full', l: 'Full-day', icon: Activity },
];

const DRAFT_KEY = 'utilization_draft';
const QUEUE_KEY = 'utilization_queue';

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };

const Utilizations = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [tab, setTab] = useState('overview');
  const [vehicles, setVehicles] = useState([]);
  const [operators, setOperators] = useState([]);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [from, setFrom] = useState(daysAgo(30));
  const [to, setTo] = useState(today());
  const [vehicleFilter, setVehicleFilter] = useState('all');
  const [standardHours, setStandardHours] = useState(8);
  const [online, setOnline] = useState(navigator.onLine);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const canEdit = hasRole(user, 'super_admin', 'admin', 'supervisor', 'operator');
  const canModify = hasRole(user, 'super_admin', 'admin', 'supervisor');
  const canDelete = hasRole(user, 'super_admin', 'admin');

  const params = useMemo(() => {
    const p = { from, to };
    if (vehicleFilter !== 'all') p.vehicle_id = vehicleFilter;
    return p;
  }, [from, to, vehicleFilter]);

  const load = useCallback(async () => {
    try {
      const [vs, ops, list, sum] = await Promise.all([
        api.get('/vehicles'),
        api.get('/operators').catch(() => ({ data: [] })),
        api.get('/utilizations', { params }),
        api.get('/utilizations/summary', { params: { ...params, standard_hours_per_day: standardHours } }),
      ]);
      setVehicles(vs.data); setOperators(ops.data); setItems(list.data); setSummary(sum.data);
    } catch (e) { console.warn('Utilizations offline load:', e?.message || e); /* offline ? fall through */ }
  }, [params, standardHours]);

  useEffect(() => { load(); }, [load]);

  // Offline detection + queue drain
  useEffect(() => {
    const onOnline = () => { setOnline(true); flushQueue(); load(); };
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline); window.addEventListener('offline', onOffline);
    return () => { window.removeEventListener('online', onOnline); window.removeEventListener('offline', onOffline); };
    // eslint-disable-next-line
  }, [load]);

  const flushQueue = useCallback(async () => {
    const raw = localStorage.getItem(QUEUE_KEY);
    if (!raw) return;
    let queue = [];
    try { queue = JSON.parse(raw) || []; } catch (e) { queue = []; }
    if (!queue.length) return;
    const remaining = [];
    for (const it of queue) {
      try { await api.post('/utilizations', it.body); } catch (e) { remaining.push(it); }
    }
    localStorage.setItem(QUEUE_KEY, JSON.stringify(remaining));
    if (queue.length > remaining.length) toast({ title: `${queue.length - remaining.length} offline shift(s) synced` });
  }, [toast]);

  const vehById = (id) => vehicles.find((v) => v.id === id) || {};
  const opById = (id) => operators.find((o) => o.id === id);

  const openNew = () => { setEditing(null); setOpen(true); };
  const openEdit = (m) => { setEditing(m); setOpen(true); };

  const submit = async (body) => {
    if (editing) {
      try { await api.put(`/utilizations/${editing.id}`, body); toast({ title: 'Updated' }); }
      catch (e) { toast({ title: 'Error', description: e.response?.data?.detail, variant: 'destructive' }); return; }
    } else if (online) {
      try { await api.post('/utilizations', body); toast({ title: 'Shift logged' }); }
      catch (e) { toast({ title: 'Error', description: e.response?.data?.detail, variant: 'destructive' }); return; }
    } else {
      const raw = localStorage.getItem(QUEUE_KEY);
      const queue = (() => { try { return JSON.parse(raw) || []; } catch (e) { return []; } })();
      queue.push({ body, queued_at: new Date().toISOString() });
      localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
      toast({ title: 'Saved offline', description: 'Will sync when online.' });
    }
    localStorage.removeItem(DRAFT_KEY);
    setOpen(false); load();
  };

  const remove = async (m) => { if (!window.confirm('Delete shift record?')) return; await api.delete(`/utilizations/${m.id}`); load(); };

  const exportCSV = () => {
    const headers = ['reference_number','date','shift','equipment_code','operator','hours_used','productive_hours','idle_hours','fuel_consumed','odometer_start','odometer_end','kilometers','fuel_per_hour','km_per_liter','project_location','remarks'];
    const rows = items.map((u) => {
      const v = vehById(u.vehicle_id);
      const op = opById(u.operator_id);
      const fph = u.hours_used ? (Number(u.fuel_consumed || 0) / Number(u.hours_used)).toFixed(2) : '';
      const kpl = u.fuel_consumed ? (Number(u.kilometers || 0) / Number(u.fuel_consumed)).toFixed(2) : '';
      const obj = { ...u, equipment_code: v.equipment_code || '', operator: op?.full_name || '', fuel_per_hour: fph, km_per_liter: kpl };
      return headers.map((h) => `"${String(obj[h] ?? '').replace(/"/g, '""')}"`).join(',');
    });
    const blob = new Blob([[headers.join(','), ...rows].join('\n')], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'utilizations.csv'; a.click();
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Utilizations"
        description="Capture every shift: hours used, fuel burned, odometer in/out. ESD turns those raw numbers into utilization & efficiency KPIs you can act on."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {online ? (
              <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 px-2 py-1 rounded text-xs"><Wifi className="h-3.5 w-3.5" /> Online</span>
            ) : (
              <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 px-2 py-1 rounded text-xs"><WifiOff className="h-3.5 w-3.5" /> Offline — queued</span>
            )}
            <Button size="sm" variant="outline" onClick={() => load()} data-testid="util-refresh"><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
            {canEdit && (
              <Button size="sm" onClick={openNew} className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]" data-testid="util-log-shift">
                <Plus className="h-4 w-4 mr-1" /> Log Shift
              </Button>
            )}
          </div>
        }
      />

      {/* Filters */}
      <Card><CardContent className="p-3 flex flex-wrap items-end gap-3">
        <div><Label className="text-xs">From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 text-sm w-40" data-testid="util-filter-from" /></div>
        <div><Label className="text-xs">To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-8 text-sm w-40" data-testid="util-filter-to" /></div>
        <div className="min-w-[220px]">
          <Label className="text-xs">Equipment</Label>
          <Select value={vehicleFilter} onValueChange={setVehicleFilter}>
            <SelectTrigger className="h-8 text-sm" data-testid="util-filter-vehicle"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All equipment</SelectItem>
              {vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.equipment_code} · {v.description}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Std hrs / day</Label><Input type="number" min="1" max="24" value={standardHours} onChange={(e) => setStandardHours(Number(e.target.value) || 8)} className="h-8 text-sm w-24" data-testid="util-std-hours" /></div>
      </CardContent></Card>

      {/* KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Tile icon={Clock} label="Total hours" value={formatNumber(summary?.total_hours || 0)} sub={`${summary?.shifts || 0} shifts — ${summary?.days || 0} days`} accent="primary" testId="kpi-hours" />
        <Tile icon={Activity} label="Utilization rate" value={summary?.overall_utilization_pct != null ? `${summary.overall_utilization_pct}%` : '—'} sub={`vs ${standardHours} hrs/day`} accent={summary?.overall_utilization_pct >= 70 ? 'emerald' : summary?.overall_utilization_pct >= 40 ? 'amber' : 'red'} testId="kpi-util" />
        <Tile icon={Fuel} label="Fuel / hour" value={summary?.avg_fuel_per_hour != null ? `${formatNumber(summary.avg_fuel_per_hour)} L` : '—'} sub={`${formatNumber(summary?.total_fuel || 0)} L total`} accent="indigo" testId="kpi-fph" />
        <Tile icon={Gauge} label="Km / liter" value={summary?.avg_km_per_liter != null ? `${formatNumber(summary.avg_km_per_liter)}` : '—'} sub={`${formatNumber(summary?.total_km || 0)} km total`} accent="emerald" testId="kpi-kpl" />
        <Tile icon={TrendingUp} label="Productive hrs" value={formatNumber(summary?.total_productive_hours || 0)} sub={`${formatNumber(summary?.total_idle_hours || 0)} idle`} accent="amber" testId="kpi-productive" />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {[
          ['overview', 'Overview'],
          ['log', `Shift Log (${items.length})`],
          ['equipment', 'By Equipment'],
        ].map(([k, lbl]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === k ? 'border-[hsl(217,91%,24%)] text-[hsl(217,91%,24%)]' : 'border-transparent text-slate-500 hover:text-slate-900'}`} data-testid={`util-tab-${k}`}>
            {lbl}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab summary={summary} vehicles={vehicles} standardHours={standardHours} />}
      {tab === 'log' && (
        <LogTable
          items={items} vehById={vehById} opById={opById} canModify={canModify} canDelete={canDelete}
          onEdit={openEdit} onDelete={remove} onExport={exportCSV}
        />
      )}
      {tab === 'equipment' && <EquipmentBreakdown summary={summary} vehicles={vehicles} />}

      <ShiftDialog
        open={open} editing={editing} vehicles={vehicles} operators={operators}
        onClose={() => setOpen(false)}
        onSubmit={submit}
      />
    </div>
  );
};

/* ---------------- OVERVIEW (educational landing) ---------------- */
const OverviewTab = ({ summary, vehicles, standardHours }) => {
  const vehById = (id) => vehicles.find((v) => v.id === id);
  const top = (summary?.per_vehicle || []).slice(0, 5);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card className="lg:col-span-2">
        <CardContent className="p-6 space-y-5">
          <div>
            <div className="text-xs uppercase tracking-wider text-[hsl(217,91%,24%)] font-semibold flex items-center gap-2"><Sparkles className="h-3.5 w-3.5" /> Why log shifts</div>
            <h2 className="text-2xl font-bold text-slate-900 mt-1">A piece of equipment you don&rsquo;t measure is a piece of equipment you don&rsquo;t own.</h2>
            <p className="text-sm text-slate-600 mt-2">Every shift produces three free signals — <strong>hours used</strong>, <strong>fuel burned</strong>, and the <strong>odometer delta</strong>. Together they tell you which assets earn their keep, which are sitting idle, and which are bleeding diesel for no output. Capture them once per shift; ESD turns them into the KPIs you brief management with.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              { icon: Activity, t: 'Utilization rate', d: 'Productive hours — available hours. Below 40% means parked iron. Above 90% means burnout risk.' },
              { icon: Fuel, t: 'Fuel per hour (L/h)', d: 'Spot a unit burning 30% more than its peers — it’s either misused, overloaded, or due for service.' },
              { icon: Gauge, t: 'Kilometers per liter', d: 'Long-haul efficiency. Trend it monthly to catch tire pressure or driver-habit regressions early.' },
              { icon: AlertTriangle, t: 'Idle vs. productive', d: 'Two trucks with the same hours don’t cost the same to run. Idle hours are real fuel & wear.' },
              { icon: BarChart3, t: 'Per-asset cost trend', d: 'Pairs with the Consumables module to give you total cost of ownership per equipment.' },
              { icon: Sparkles, t: 'Operator coaching', d: 'Compare same equipment across operators — surfaces training wins, not blame.' },
            ].map((b) => (
              <div key={b.t} className="p-3 rounded-md border border-slate-200 bg-slate-50/50">
                <div className="flex items-center gap-2 text-slate-900 font-semibold text-sm"><b.icon className="h-4 w-4 text-[hsl(217,91%,24%)]" /> {b.t}</div>
                <p className="text-xs text-slate-600 mt-1">{b.d}</p>
              </div>
            ))}
          </div>

          <div>
            <h3 className="text-sm font-bold tracking-wider uppercase text-slate-700">What you capture per shift</h3>
            <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              {['Date', 'Shift (day/night/full)', 'Equipment', 'Operator', 'Hours used', 'Idle hours (optional)', 'Fuel consumed (L)', 'Odometer start', 'Odometer end', 'Kilometers (auto)', 'Project location', 'Remarks']
                .map((f) => <div key={f} className="px-2 py-1.5 bg-white border border-slate-200 rounded font-medium text-slate-700">{f}</div>)}
            </div>
            <p className="text-[11px] text-slate-500 mt-2 italic">Kilometers = odometer_end — odometer_start (auto-computed). Productive hours = hours_used — idle_hours.</p>
          </div>

          <Alert className="bg-blue-50 border-blue-200 text-blue-900">
            <AlertDescription className="text-xs">
              <strong>Offline-first.</strong> The Log Shift form auto-saves a draft to your device. If your site has no signal, your submission queues locally and syncs the moment you&rsquo;re back online.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      <Card><CardContent className="p-5">
        <div className="text-xs uppercase tracking-wider text-slate-500">Top equipment — hours</div>
        {top.length === 0 ? (
          <div className="mt-2 text-xs text-slate-500">No shifts in this period.</div>
        ) : (
          <ul className="mt-2 space-y-2 text-sm">
            {top.map((r) => {
              const v = vehById(r.vehicle_id);
              return (
                <li key={r.vehicle_id} className="flex items-center justify-between border-b last:border-0 pb-1.5">
                  <div className="min-w-0">
                    <div className="font-mono font-semibold truncate">{v?.equipment_code || '—'}</div>
                    <div className="text-[10px] text-slate-500">{r.shifts} shifts — {formatNumber(r.hours_used)} hrs — {r.utilization_rate_pct != null ? `${r.utilization_rate_pct}%` : '—'} util</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-xs">{r.fuel_per_hour ?? '—'} L/h</div>
                    <div className="text-[10px] text-slate-500">{r.km_per_liter ?? '—'} km/L</div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        <div className="mt-4 pt-3 border-t text-[10px] text-slate-500 italic">Standard productivity benchmark: <strong>{standardHours} hours/day</strong>. Adjust above to match your operation.</div>
      </CardContent></Card>
    </div>
  );
};

/* ---------------- SHIFT LOG TABLE ---------------- */
const LogTable = ({ items, vehById, opById, canModify, canDelete, onEdit, onDelete, onExport }) => (
  <Card><CardContent className="p-0">
    <div className="flex items-center justify-between p-3 border-b">
      <div className="text-sm text-slate-600">{items.length} shift records</div>
      <Button size="sm" variant="outline" onClick={onExport} disabled={items.length === 0} data-testid="util-export"><Download className="h-3.5 w-3.5 mr-1" /> Export CSV</Button>
    </div>
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b text-left text-slate-600">
          <tr>
            <th className="p-3">Ref</th><th className="p-3">Date</th><th className="p-3">Shift</th>
            <th className="p-3">Equipment</th><th className="p-3">Operator</th>
            <th className="p-3">Hours</th><th className="p-3">Fuel (L)</th>
            <th className="p-3">Odometer</th><th className="p-3">km</th>
            <th className="p-3">L/h</th><th className="p-3">km/L</th>
            <th className="p-3 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {items.length === 0 ? (
            <tr><td colSpan={12} className="p-10 text-center text-slate-500">No shifts logged in this period. Click <strong>Log Shift</strong> to start.</td></tr>
          ) : items.map((u) => {
            const v = vehById(u.vehicle_id);
            const op = opById(u.operator_id);
            const fph = u.hours_used ? (Number(u.fuel_consumed || 0) / Number(u.hours_used)) : null;
            const kpl = u.fuel_consumed ? (Number(u.kilometers || 0) / Number(u.fuel_consumed)) : null;
            return (
              <tr key={u.id} className="table-row-hover" data-testid={`util-row-${u.id}`}>
                <td className="p-3 font-mono text-xs">{u.reference_number || '—'}</td>
                <td className="p-3 text-xs">{formatDate(u.date)}</td>
                <td className="p-3"><Badge variant="secondary" className="text-[10px]">{u.shift || 'day'}</Badge></td>
                <td className="p-3">{v?.equipment_code ? <div><div className="font-mono text-xs font-semibold">{v.equipment_code}</div><div className="text-[10px] text-slate-500">{v.description}</div></div> : <span className="text-slate-400 text-xs">—</span>}</td>
                <td className="p-3 text-xs">{op?.full_name || '—'}</td>
                <td className="p-3 text-xs">{formatNumber(u.hours_used)} <span className="text-[10px] text-slate-400">({formatNumber(u.productive_hours)} prod)</span></td>
                <td className="p-3 text-xs">{formatNumber(u.fuel_consumed)}</td>
                <td className="p-3 text-[10px] text-slate-500">{u.odometer_start != null ? `${formatNumber(u.odometer_start)} — ${formatNumber(u.odometer_end)}` : '—'}</td>
                <td className="p-3 font-mono text-xs">{formatNumber(u.kilometers || 0)}</td>
                <td className="p-3 font-mono text-xs">{fph ? fph.toFixed(2) : '—'}</td>
                <td className="p-3 font-mono text-xs">{kpl ? kpl.toFixed(2) : '—'}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  {canModify && <Button size="sm" variant="ghost" onClick={() => onEdit(u)} data-testid={`util-edit-${u.id}`}>Edit</Button>}
                  {canDelete && <Button size="sm" variant="ghost" onClick={() => onDelete(u)} data-testid={`util-del-${u.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </CardContent></Card>
);

/* ---------------- EQUIPMENT BREAKDOWN ---------------- */
const EquipmentBreakdown = ({ summary, vehicles }) => {
  const vehById = (id) => vehicles.find((v) => v.id === id);
  const rows = summary?.per_vehicle || [];
  return (
    <Card><CardContent className="p-0">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b text-left text-slate-600">
            <tr>
              <th className="p-3">Equipment</th><th className="p-3">Shifts</th><th className="p-3">Days</th>
              <th className="p-3">Hours</th><th className="p-3">Productive</th><th className="p-3">Idle</th>
              <th className="p-3">Fuel (L)</th><th className="p-3">km</th>
              <th className="p-3">L/h</th><th className="p-3">km/L</th><th className="p-3">Util %</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.length === 0 ? <tr><td colSpan={11} className="p-10 text-center text-slate-500">Log at least one shift to build the breakdown.</td></tr> :
              rows.map((r) => {
                const v = vehById(r.vehicle_id);
                const util = r.utilization_rate_pct;
                const tone = util == null ? 'text-slate-500' : util >= 70 ? 'text-emerald-700' : util >= 40 ? 'text-amber-700' : 'text-red-700';
                return (
                  <tr key={r.vehicle_id} className="table-row-hover" data-testid={`util-veh-row-${r.vehicle_id}`}>
                    <td className="p-3"><div className="font-mono text-xs font-semibold">{v?.equipment_code || '—'}</div><div className="text-[10px] text-slate-500">{v?.description}</div></td>
                    <td className="p-3 text-xs">{r.shifts}</td>
                    <td className="p-3 text-xs">{r.days}</td>
                    <td className="p-3 font-mono text-xs">{formatNumber(r.hours_used)}</td>
                    <td className="p-3 font-mono text-xs">{formatNumber(r.productive_hours)}</td>
                    <td className="p-3 font-mono text-xs">{formatNumber(r.idle_hours)}</td>
                    <td className="p-3 font-mono text-xs">{formatNumber(r.fuel_consumed)}</td>
                    <td className="p-3 font-mono text-xs">{formatNumber(r.kilometers)}</td>
                    <td className="p-3 font-mono text-xs">{r.fuel_per_hour ?? '—'}</td>
                    <td className="p-3 font-mono text-xs">{r.km_per_liter ?? '—'}</td>
                    <td className={`p-3 font-mono text-xs font-semibold ${tone}`}>{util != null ? `${util}%` : '—'}</td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </CardContent></Card>
  );
};

/* ---------------- SHIFT DIALOG ---------------- */
const useDraft = (initial, enabled) => {
  const [data, setData] = useState(() => {
    if (!enabled) return initial;
    try { const d = localStorage.getItem(DRAFT_KEY); return d ? { ...initial, ...JSON.parse(d) } : initial; } catch (e) { return initial; }
  });
  const timer = useRef(null);
  const set = (k, v) => setData((p) => {
    const next = { ...p, [k]: v };
    if (enabled) {
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => localStorage.setItem(DRAFT_KEY, JSON.stringify(next)), 600);
    }
    return next;
  });
  return [data, set, setData];
};

const ShiftDialog = ({ open, editing, vehicles, operators, onClose, onSubmit }) => {
  const initial = useMemo(() => editing ? { ...editing } : {
    vehicle_id: '', mobilization_id: '', operator_id: '', date: today(), shift: 'day',
    hours_used: 8, idle_hours: 0, fuel_consumed: 0, odometer_start: '', odometer_end: '',
    project_location: '', remarks: '',
  }, [editing]);
  const [form, setField] = useDraft(initial, !editing);

  // Reset when dialog opens with different editing context
  useEffect(() => { if (open && editing) Object.entries(initial).forEach(([k, v]) => setField(k, v)); /* eslint-disable-next-line */ }, [open, editing?.id]);

  const km = useMemo(() => {
    const s = Number(form.odometer_start); const e = Number(form.odometer_end);
    if (Number.isFinite(s) && Number.isFinite(e) && e >= s) return e - s;
    return null;
  }, [form.odometer_start, form.odometer_end]);

  const productive = useMemo(() => Math.max(Number(form.hours_used || 0) - Number(form.idle_hours || 0), 0), [form.hours_used, form.idle_hours]);
  const fph = form.hours_used ? Number(form.fuel_consumed || 0) / Number(form.hours_used) : null;
  const kpl = (form.fuel_consumed && km != null) ? km / Number(form.fuel_consumed) : null;

  const handleSubmit = () => {
    const body = {
      vehicle_id: form.vehicle_id, mobilization_id: form.mobilization_id || null,
      operator_id: form.operator_id || null, date: form.date, shift: form.shift,
      hours_used: Number(form.hours_used) || 0, idle_hours: Number(form.idle_hours) || 0,
      productive_hours: productive,
      fuel_consumed: Number(form.fuel_consumed) || 0,
      odometer_start: form.odometer_start ? Number(form.odometer_start) : null,
      odometer_end: form.odometer_end ? Number(form.odometer_end) : null,
      project_location: form.project_location || null, remarks: form.remarks || null,
    };
    onSubmit(body);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Clock className="h-5 w-5" /> {editing ? 'Edit Shift' : 'Log Shift'}</DialogTitle>
          <DialogDescription>Capture the equipment&rsquo;s entire shift in one go. ESD computes kilometers, productive hours, fuel/hour and km/L automatically.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Equipment *</Label>
            <Select value={form.vehicle_id} onValueChange={(v) => setField('vehicle_id', v)}>
              <SelectTrigger data-testid="shift-vehicle"><SelectValue placeholder="Select equipment" /></SelectTrigger>
              <SelectContent>{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.equipment_code} · {v.description}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div><Label>Date *</Label><Input type="date" value={form.date || ''} onChange={(e) => setField('date', e.target.value)} data-testid="shift-date" /></div>
          <div><Label>Shift</Label>
            <Select value={form.shift || 'day'} onValueChange={(v) => setField('shift', v)}>
              <SelectTrigger data-testid="shift-type"><SelectValue /></SelectTrigger>
              <SelectContent>{SHIFTS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div><Label>Operator</Label>
            <Select value={form.operator_id || 'none'} onValueChange={(v) => setField('operator_id', v === 'none' ? '' : v)}>
              <SelectTrigger data-testid="shift-operator"><SelectValue placeholder="(optional)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— None —</SelectItem>
                {operators.map((o) => <SelectItem key={o.id} value={o.id}>{o.full_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Location</Label><Input value={form.project_location || ''} onChange={(e) => setField('project_location', e.target.value)} data-testid="shift-location" /></div>

          <div><Label>Hours Used *</Label><Input type="number" step="0.1" value={form.hours_used} onChange={(e) => setField('hours_used', e.target.value)} data-testid="shift-hours" /></div>
          <div><Label>Idle Hours</Label><Input type="number" step="0.1" value={form.idle_hours} onChange={(e) => setField('idle_hours', e.target.value)} data-testid="shift-idle" /></div>

          <div><Label>Fuel Consumed (L)</Label><Input type="number" step="0.1" value={form.fuel_consumed} onChange={(e) => setField('fuel_consumed', e.target.value)} data-testid="shift-fuel" /></div>
          <div><Label>Productive Hrs (auto)</Label><Input value={productive} readOnly className="bg-slate-50 font-mono" data-testid="shift-productive" /></div>

          <div><Label>Odometer Start</Label><Input type="number" value={form.odometer_start || ''} onChange={(e) => setField('odometer_start', e.target.value)} data-testid="shift-odo-start" /></div>
          <div><Label>Odometer End</Label><Input type="number" value={form.odometer_end || ''} onChange={(e) => setField('odometer_end', e.target.value)} data-testid="shift-odo-end" /></div>

          {/* Live KPI preview */}
          <div className="col-span-2 grid grid-cols-3 gap-2 p-3 rounded-md bg-slate-50 border border-slate-200">
            <Stat icon={Gauge} label="Kilometers" value={km != null ? formatNumber(km) : '—'} testId="shift-km" />
            <Stat icon={Fuel} label="Fuel / hour" value={fph != null && Number.isFinite(fph) ? `${fph.toFixed(2)} L/h` : '—'} testId="shift-fph" />
            <Stat icon={Activity} label="km / liter" value={kpl != null && Number.isFinite(kpl) ? kpl.toFixed(2) : '—'} testId="shift-kpl" />
          </div>

          <div className="col-span-2"><Label>Remarks</Label><Textarea rows={2} value={form.remarks || ''} onChange={(e) => setField('remarks', e.target.value)} data-testid="shift-remarks" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={!form.vehicle_id || !form.date} className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]" data-testid="shift-submit"><Save className="h-4 w-4 mr-1" /> {editing ? 'Update Shift' : 'Save Shift'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Stat = ({ icon: Icon, label, value, testId }) => (
  <div data-testid={testId}>
    <div className="text-[10px] uppercase text-slate-500 flex items-center gap-1"><Icon className="h-3 w-3" /> {label}</div>
    <div className="text-sm font-semibold font-mono">{value}</div>
  </div>
);

const Tile = ({ icon: Icon, label, value, sub, accent, testId }) => {
  const tone = { primary: 'bg-blue-50 text-blue-700 border-blue-200', emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200', indigo: 'bg-indigo-50 text-indigo-700 border-indigo-200', amber: 'bg-amber-50 text-amber-700 border-amber-200', red: 'bg-red-50 text-red-700 border-red-200', slate: 'bg-slate-50 text-slate-700 border-slate-200' }[accent] || 'bg-slate-50 text-slate-700 border-slate-200';
  return (
    <div className="rounded-lg border bg-white p-3 flex items-center gap-3 border-slate-200" data-testid={testId}>
      <div className={`h-10 w-10 rounded-md grid place-items-center border ${tone}`}><Icon className="h-5 w-5" /></div>
      <div className="min-w-0">
        <div className="text-[11px] text-slate-500 truncate">{label}</div>
        <div className="text-xl font-bold text-slate-900 leading-tight truncate">{value}</div>
        {sub && <div className="text-[10px] text-slate-400 truncate">{sub}</div>}
      </div>
    </div>
  );
};

export default Utilizations;
